/**
 * Registration Slice for Wadakaroo
 *
 * Manages the multi-step registration flow:
 * Step 1: Email registration (handled in authSlice)
 * Step 2: Complete user details (first name, last name, phone, etc.)
 * Step 3: Provision organisation (org name, type, etc.)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    completeRegistrationApi, provisionOrganisationApi, refreshTokenApi,
} from '../../services/api';
import {
    getRefreshToken, setTokens, parseToken, setUser as persistUserToStorage,
} from '../../services/authService';

// ============================================
// Async Thunks
// ============================================

/**
 * Step 2: Complete user registration with personal details
 */
export const completeRegistration = createAsyncThunk(
    'registration/completeRegistration',
    async (userDetails, { rejectWithValue }) => {
        try {
            const data = await completeRegistrationApi(userDetails);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to complete registration. Please try again.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Step 3: Provision organisation
 */
export const provisionOrganisation = createAsyncThunk(
    'registration/provisionOrganisation',
    async (orgDetails, { rejectWithValue }) => {
        try {
            const data = await provisionOrganisationApi(orgDetails);

            // Swap the session token for one that knows about the workshop.
            //
            // The token in hand was minted before the organisation existed, so it has no
            // organisationUuid and — because the sidebar is rendered from the token's
            // uiViews claim — no navigation either. That is why setup used to end by
            // asking the user to sign out and back in.
            //
            // The refresh endpoint rebuilds its claims from the database (see
            // SessionAttributesFactory on the backend), so refreshing here produces
            // exactly the token a fresh login would, and the sign-out step disappears.
            //
            // A failure here is NOT a failed setup: the workshop is already created and
            // committed. `sessionRefreshed: false` tells the UI to fall back to asking
            // for a manual sign-in rather than reporting an error for something that
            // actually succeeded.
            let sessionRefreshed = false;
            let user = null;
            try {
                const refreshToken = getRefreshToken();
                if (refreshToken) {
                    const tokens = await refreshTokenApi(refreshToken);
                    setTokens(tokens.accessToken, tokens.refreshToken);
                    user = parseToken(tokens.accessToken);
                    if (user) {
                        persistUserToStorage(user);
                        sessionRefreshed = true;
                    }
                }
            } catch (refreshError) {
                console.warn('Organisation created, but the session could not be refreshed:',
                    refreshError?.response?.status);
            }

            return { ...data, sessionRefreshed, user };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to create organisation. Please try again.';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    // Current step in registration flow
    currentStep: 1, // 1 = email, 2 = user details, 3 = org provisioning

    // Step 2: User details form data
    userDetails: {
        firstName: '',
        lastName: '',
        phone: '',
    },

    // Step 3: Organisation details form data
    orgDetails: {
        organisationName: '',
        orgAddress: '',
        orgCity: '',
        orgState: '',
        orgPostcode: '',
        orgCountry: 'AU',
    },

    // Status
    isSubmitting: false,
    error: null,
    userDetailsComplete: false,
    orgProvisioningComplete: false,
};

const registrationSlice = createSlice({
    name: 'registration',
    initialState,
    reducers: {
        setCurrentStep: (state, action) => {
            state.currentStep = action.payload;
        },
        updateUserDetails: (state, action) => {
            state.userDetails = { ...state.userDetails, ...action.payload };
        },
        updateOrgDetails: (state, action) => {
            state.orgDetails = { ...state.orgDetails, ...action.payload };
        },
        clearRegistrationError: (state) => {
            state.error = null;
        },
        resetRegistration: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        // ---- Complete Registration ----
        builder.addCase(completeRegistration.pending, (state) => {
            state.isSubmitting = true;
            state.error = null;
        });
        builder.addCase(completeRegistration.fulfilled, (state) => {
            state.isSubmitting = false;
            state.userDetailsComplete = true;
            state.currentStep = 3;
            state.error = null;
        });
        builder.addCase(completeRegistration.rejected, (state, action) => {
            state.isSubmitting = false;
            state.error = action.payload || 'Failed to complete registration';
        });

        // ---- Provision Organisation ----
        builder.addCase(provisionOrganisation.pending, (state) => {
            state.isSubmitting = true;
            state.error = null;
        });
        builder.addCase(provisionOrganisation.fulfilled, (state) => {
            state.isSubmitting = false;
            state.orgProvisioningComplete = true;
            state.error = null;
        });
        builder.addCase(provisionOrganisation.rejected, (state, action) => {
            state.isSubmitting = false;
            state.error = action.payload || 'Failed to provision organisation';
        });
    },
});

// ============================================
// Actions
// ============================================

export const {
    setCurrentStep,
    updateUserDetails,
    updateOrgDetails,
    clearRegistrationError,
    resetRegistration,
} = registrationSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectCurrentStep = (state) => state.registration.currentStep;
export const selectUserDetails = (state) => state.registration.userDetails;
export const selectOrgDetails = (state) => state.registration.orgDetails;
export const selectIsSubmitting = (state) => state.registration.isSubmitting;
export const selectRegistrationError = (state) => state.registration.error;
export const selectUserDetailsComplete = (state) => state.registration.userDetailsComplete;
export const selectOrgProvisioningComplete = (state) => state.registration.orgProvisioningComplete;

export default registrationSlice.reducer;